// Ensure the class is only defined if it doesn't exist,
// preventing errors if the script is loaded multiple times (e.g., in Shopify theme editor).
if (!customElements.get('td-size-chart')) {
  class TdSizeChart extends HTMLElement {
    constructor() {
      super();

      // Data properties
      this.sizeChartData = [];
      this.legacyData = [];

      // Modal DOM element references
      this.triggerImage = null;
      this.modalOverlay = null;
      this.modalContent = null;
      this.modalImageElement = null;
      this.modalCloseButton = null;
      this.modalHeadingId = `td-size-chart-heading-${Math.random().toString(36).substr(2, 9)}`; // Unique ID for ARIA
      this.externalAnchorElement = null; // To store the external anchor for the trigger

      // State
      this.previouslyFocusedElement = null;

      // Bind methods
      this.openModal = this.openModal.bind(this);
      this.closeModal = this.closeModal.bind(this);
      this._handleEscKey = this._handleEscKey.bind(this);

      // Ensure CSS for the modal is injected once
      this._ensureModalStyles();

      // Parse data from script tags
      this._parseJsonData();
    }

    // _handleOverlayClick is defined within _createModalElements's event listener
    // If it needs to be removable, it should be a bound method.
    // For now, it's an anonymous function, so direct removal in disconnectedCallback is tricky for that specific listener.
    // However, the primary listeners (trigger, close button, ESC key) are handled.

    _parseJsonData() {
      const sizeChartDataScript = this.querySelector('#td-size-charts--data');
      if (sizeChartDataScript) {
        try {
          this.sizeChartData = JSON.parse(sizeChartDataScript.textContent);
        } catch (e) {
          console.error('TdSizeChart: Error parsing JSON from #td-size-charts--data:', e);
          this.sizeChartData = [];
        }
      }

      const legacyDataScript = this.querySelector('#td-size-charts--legacy-data');
      if (legacyDataScript) {
        try {
          this.legacyData = JSON.parse(legacyDataScript.textContent);
        } catch (e) {
          console.error('TdSizeChart: Error parsing JSON from #td-size-charts--legacy-data:', e);
          this.legacyData = [];
        }
      }
    }

    connectedCallback() {
      this.generateSizeChart();
    }

    disconnectedCallback() {
      // Clean up event listeners
      if (this.triggerImage) {
        this.triggerImage.removeEventListener('click', this.openModal);
        // Remove trigger from DOM
        if (this.triggerImage.parentElement) {
          this.triggerImage.remove();
        }
      }
      if (this.modalCloseButton) {
        this.modalCloseButton.removeEventListener('click', this.closeModal);
      }
      // The overlay click listener is an anonymous function in _createModalElements.
      // If robust cleanup of this specific listener is needed for rapid add/remove scenarios,
      // it would require refactoring to use a bound, named function.

      // Remove modal overlay from body
      if (this.modalOverlay && this.modalOverlay.parentElement === document.body) {
        this.modalOverlay.remove();
      }

      // Ensure Esc key listener is removed if modal was open during disconnect
      document.removeEventListener('keydown', this._handleEscKey);
      // Ensure body class is removed
      document.body.classList.remove('td-modal-open');
    }

    _ensureModalStyles() {
      const styleId = 'td-size-chart-modal-styles';
      if (document.getElementById(styleId)) {
        return; // Styles already injected
      }

      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .td-size-chart__trigger-text {
          background: none;
          border: none;
          padding: 0;
          font: inherit;
          color: var(--text-color, #000); /* Fallback to black if --text-color is not defined */
          text-decoration: underline;
          cursor: pointer;
          display: inline-block; /* Or block, depending on desired layout */
        }
        .td-size-chart__trigger-text:hover,
        .td-size-chart__trigger-text:focus {
          /* Optional: Add hover/focus styles, e.g., different color or no underline */
          text-decoration: none;
        }
        .td-size-chart__modal {
          display: none; /* Hidden by default */
          position: fixed;
          z-index: 10000; /* High z-index */
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          overflow: auto;
          background-color: rgba(0,0,0,0.75); /* Darker overlay */
          align-items: center;
          justify-content: center;
        }
        .td-size-chart__modal--active {
          display: flex;
        }
        .td-size-chart__modal-content {
          position: relative;
          background-color: #fff;
          margin: auto;
          padding: 25px;
          border: 1px solid #bbb;
          width: 90%;
          max-width: 800px; /* Max width for the chart image */
          box-shadow: 0 5px 15px rgba(0,0,0,0.3);
          animation-name: td-modal-animatetop;
          animation-duration: 0.3s;
          border-radius: 8px;
        }
        @keyframes td-modal-animatetop {
          from {transform: translateY(-50px); opacity: 0}
          to {transform: translateY(0); opacity: 1}
        }
        .td-size-chart__modal-close-button {
          color: #333;
          position: absolute;
          top: 10px;
          right: 15px;
          font-size: 32px;
          font-weight: bold;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          line-height: 1;
        }
        .td-size-chart__modal-close-button:hover,
        .td-size-chart__modal-close-button:focus {
          color: #000;
        }
        .td-size-chart__modal-image {
          display: block;
          width: 100%;
          height: auto;
          max-height: 80vh; /* Limit image height */
          object-fit: contain;
          border-radius: 4px; /* Slight rounding for the image itself */
        }
        .td-size-chart__modal-heading {
          /* Visually hidden but accessible to screen readers */
          border: 0;
          clip: rect(0 0 0 0);
          height: 1px;
          margin: -1px;
          overflow: hidden;
          padding: 0;
          position: absolute;
          width: 1px;
        }
        body.td-modal-open {
          overflow: hidden;
        }
      `;
      document.head.appendChild(style);
    }

    _createModalElements(chartData) {
      // Modal Overlay (Container)
      this.modalOverlay = document.createElement('div');
      this.modalOverlay.classList.add('td-size-chart__modal');
      this.modalOverlay.setAttribute('aria-hidden', 'true');
      this.modalOverlay.setAttribute('role', 'dialog');
      this.modalOverlay.setAttribute('aria-modal', 'true');
      this.modalOverlay.setAttribute('aria-labelledby', this.modalHeadingId);
      this.modalOverlay.addEventListener('click', (event) => {
        // Close if clicked outside the modal content
        if (event.target === this.modalOverlay) {
          this.closeModal();
        }
      });

      // Modal Content Box
      this.modalContent = document.createElement('div');
      this.modalContent.classList.add('td-size-chart__modal-content');

      // Hidden Heading for ARIA
      const heading = document.createElement('h2');
      heading.id = this.modalHeadingId;
      heading.classList.add('td-size-chart__modal-heading');
      heading.textContent = chartData.altText || 'Size Chart'; // Use alt text for heading
      this.modalContent.appendChild(heading);

      // Close Button
      this.modalCloseButton = document.createElement('button');
      this.modalCloseButton.classList.add('td-size-chart__modal-close-button');
      this.modalCloseButton.innerHTML = '&times;'; // Use &times; for 'x'
      this.modalCloseButton.setAttribute('aria-label', 'Close size chart modal');
      this.modalCloseButton.addEventListener('click', this.closeModal);
      this.modalContent.appendChild(this.modalCloseButton);

      // Modal Image
      this.modalImageElement = document.createElement('img');
      this.modalImageElement.classList.add('td-size-chart__modal-image');
      this.modalImageElement.src = chartData.image;
      this.modalImageElement.alt = chartData.altText || 'Size chart';
      this.modalContent.appendChild(this.modalImageElement);

      this.modalOverlay.appendChild(this.modalContent);
    }

    generateSizeChart() {
      // Clear previous trigger if it was a child of this component or an external anchor
      if (this.triggerImage && this.triggerImage.parentElement) {
        this.triggerImage.remove();
        this.triggerImage = null;
      }
      // Clear previous modal overlay if it's in the body
      if (this.modalOverlay && this.modalOverlay.parentElement === document.body) {
        this.modalOverlay.remove();
        this.modalOverlay = null; 
      }
      // Clear any direct children of this component (e.g., if trigger was appended here as fallback)
      this.innerHTML = '';
      this.externalAnchorElement = null; // Reset external anchor

      const productTagsAttr = this.dataset.productTags;
      if (!productTagsAttr || productTagsAttr.trim() === '') {
        return;
      }

      const productTags = productTagsAttr.split(',').map(tag => tag.trim().toLowerCase()).filter(tag => tag !== '');
      if (productTags.length === 0) {
        return;
      }

      let chartToDisplay = null;
      if (this.sizeChartData && this.sizeChartData.length > 0) {
        for (const productTag of productTags) {
          const foundChart = this.sizeChartData.find(chart => 
            chart && typeof chart.tag === 'string' && chart.tag.trim().toLowerCase() === productTag
          );
          if (foundChart) {
            chartToDisplay = foundChart;
            break;
          }
        }
      }

      if (!chartToDisplay && this.legacyData && this.legacyData.length > 0) {
        for (const productTag of productTags) {
          const foundChart = this.legacyData.find(chart => 
            chart && typeof chart.tag === 'string' && chart.tag.trim().toLowerCase() === productTag
          );
          if (foundChart) {
            chartToDisplay = foundChart;
            break;
          }
        }
      }

      if (chartToDisplay) {
        // Create and append the text trigger
        this.triggerImage = document.createElement('button'); // Using button for better semantics
        this.triggerImage.type = 'button'; // Prevent form submission if inside a form
        this.triggerImage.textContent = 'Size Guide';
        this.triggerImage.classList.add('td-size-chart__trigger-text');
        // Set an ARIA label if more context is needed than just "Size Guide"
        // For example: this.triggerImage.setAttribute('aria-label', 'Open size guide modal');
        this.triggerImage.addEventListener('click', this.openModal);

        // Determine where to append the trigger
        const anchorId = this.dataset.anchorId;
        if (anchorId) {
          this.externalAnchorElement = document.getElementById(anchorId);
        }

        if (this.externalAnchorElement) {
          this.externalAnchorElement.appendChild(this.triggerImage);
        } else {
          this.appendChild(this.triggerImage); // Fallback to appending to self
          if (anchorId) { // Only warn if an anchorId was provided but not found
            console.warn(`TdSizeChart: Anchor element with ID '${anchorId}' not found. Appending trigger to component itself.`);
          }
        }

        // Create modal structure
        this._createModalElements(chartToDisplay);
        // Append modal overlay to the body to avoid z-index issues
        document.body.appendChild(this.modalOverlay);
      }
    }

    openModal() {
      if (!this.modalOverlay) return;

      this.previouslyFocusedElement = document.activeElement;
      this.modalOverlay.classList.add('td-size-chart__modal--active');
      this.modalOverlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('td-modal-open');
      document.addEventListener('keydown', this._handleEscKey);
      
      // Focus the close button after a short delay to ensure it's focusable
      setTimeout(() => {
        if (this.modalCloseButton) this.modalCloseButton.focus();
      }, 50);
    }

    closeModal() {
      if (!this.modalOverlay) return;

      this.modalOverlay.classList.remove('td-size-chart__modal--active');
      this.modalOverlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('td-modal-open');
      document.removeEventListener('keydown', this._handleEscKey);

      if (this.previouslyFocusedElement) {
        this.previouslyFocusedElement.focus();
      }
      this.previouslyFocusedElement = null;
    }

    _handleEscKey(event) {
      if (event.key === 'Escape') {
        this.closeModal();
      }
    }
  }

  customElements.define("td-size-chart", TdSizeChart);
}